import * as yup from 'yup';

const emailSchema = yup.string().email().required();
const phoneRegex = /^(\+|[0-9]|\s?)+$/;

export const requestFormSchema = yup.object().shape({
  emailOrPhone: yup.string().when('existingUserId', {
    is: value => !value,
    then: s =>
      s.test(
        'email-or-phone',
        'Enter a valid email or phone number',
        async value => {
          if (!value) return false;
          const isValidEmail = await emailSchema.isValid(value);
          const isValidPhone = phoneRegex.test(value);
          return isValidEmail || isValidPhone;
        }
      ),
  }),
});

/**
 * @description luhn credit card number test
 *
 * optimization hard coded vals array prevent
 * being used for other luhn applications
 * https://en.wikipedia.org/wiki/Luhn_algorithm
 *
 * @param {value} the credit card number to test
 * @returns {boolean} if valid credit card number
 */
const luhn = (value: string) => {
  const vals = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
  if (!value) return false;
  const s = value.replace(/\s/g, '').trim();
  let len = s ? s.length : 0;
  let bit = 1;
  let sum = 0;

  // eslint-disable-next-line no-plusplus
  while (len--) {
    // eslint-disable-next-line
    sum += !(bit ^= 1) ? parseInt(s[len], 10) : vals[s[len]];
  }

  return sum % 10 === 0 && sum > 0;
};

export const paymentFormSchema = yup.object().shape({
  cardNumber: yup
    .string()
    .test('is-credit-card', 'Enter a valid card number', luhn)
    .required('Card number is required'),
  expiryDate: yup
    .string()
    .matches(/\d{2}\/\d{2}/, 'Expiry date format should be MM/YY')
    .required('Expiry date is required'),
  cvv: yup
    .string()
    .min(2, 'CVV is not valid')
    .max(5, 'CVV is not valid')
    .required('Security code (CVV) is required'),
  cardName: yup
    .string()
    .matches(/\s/, { message: 'Must include first and last name' })
    .required('Card name is required'),
});

export const directDebitFormSchema = yup.object().shape({
  accountName: yup.string().required('Account name is required'),
  bsb: yup.number().required('BSB is required'),
  accountNumber: yup.number().required('Account number is required'),
  firstSignatory: yup.string().required('First signatory is required'),
  secondSignatory: yup.string(),
  acknowledgement: yup
    .boolean()
    .required('Acknowledgement is required')
    .oneOf([true], 'Acknowledgement is required'),
  defaultPayment: yup.boolean(),
  email: yup.string().when('editable', {
    is: true,
    then: s => s.required('Email is required'),
    otherwise: s => s.optional(),
  }),
  phone: yup.string().when('editable', {
    is: true,
    then: s => s.required('Phone is required'),
    otherwise: s => s.optional(),
  }),
});
